import { z } from 'zod'

import {
  AllGradeSubjectSchema,
  RevisionSchema,
  SchoolTypeSchema,
  SemesterSchema,
} from '~/@entities/SchoolSystem'
import { SignatureTypeSchema } from '~/@entities/Workbook'

/***************** 상품 종류 *****************/
export const PRODUCT_TYPE = [
  'SIGNATURE_WORKBOOK',
  'CUSTOM_WORKBOOK',
  'PRINT_DOCUMENT',
  'KMM_AWARDS',
] as const

/***************** 장바구니에 담기는 상품 종류 *****************/
// KMM 시상품은 장바구니를 거치지 않고 바로 주문한다
export const CART_PRODUCT_TYPE = [
  'SIGNATURE_WORKBOOK',
  'CUSTOM_WORKBOOK',
  'PRINT_DOCUMENT',
] as const satisfies readonly (typeof PRODUCT_TYPE)[number][]

/***************** 상품 게시 상태 *****************/
export const PRODUCT_STATUS = ['DRAFT', 'ACTIVE', 'HIDDEN', 'INACTIVE'] as const

/***************** 인쇄 범위 *****************/
export const PDF_TYPE = ['PROBLEM', 'PROBLEM_WITH_ANSWER'] as const

/***************** 내지 색 *****************/
export const PRINT_TYPE = ['COLOR', 'BW'] as const

/***************** KMM 시상품 종류 *****************/
export const AWARD_PRODUCT_TYPE = ['BANNER', 'BANNER_STAND', 'CERTIFICATE'] as const

export const ProductTypeSchema = z.enum(PRODUCT_TYPE)
export type ProductType = z.infer<typeof ProductTypeSchema>

export const CartProductTypeSchema = z.enum(CART_PRODUCT_TYPE)
export type CartProductType = z.infer<typeof CartProductTypeSchema>

export const ProductStatusSchema = z.enum(PRODUCT_STATUS)
export type ProductStatus = z.infer<typeof ProductStatusSchema>

export const PdfTypeSchema = z.enum(PDF_TYPE)
export type PdfType = z.infer<typeof PdfTypeSchema>

export const PrintTypeSchema = z.enum(PRINT_TYPE)
export type PrintType = z.infer<typeof PrintTypeSchema>

export const AwardProductTypeSchema = z.enum(AWARD_PRODUCT_TYPE)
export type AwardProductType = z.infer<typeof AwardProductTypeSchema>

/***************** 상품 *****************/
// 서버는 상품 종류마다 다른 응답 클래스를 type 판별 필드 없이 내보내고, 요청한 type 의 상품만 돌려준다.
// type 은 DB 값이라 요청한 종류와 같다.
const _ProductCommonSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string(),
  detailImageUrl: z.string(),
  status: ProductStatusSchema,
  productDiscountRate: z.number(),
})

export const SignatureWorkbookProductSchema = _ProductCommonSchema.extend({
  type: z.literal('SIGNATURE_WORKBOOK'),
  workbookId: z.number(),
  revision: RevisionSchema,
  schoolType: SchoolTypeSchema,
  grade: AllGradeSubjectSchema,
  semester: SemesterSchema,
  pairFlag: z.boolean(),
  autoScored: z.boolean(),
  signatureType: SignatureTypeSchema,
})
export type SignatureWorkbookProduct = z.infer<typeof SignatureWorkbookProductSchema>

// 페이지 단가만 담는다. problemColorPricePerPage·problemBwPricePerPage 는 서버가 colorPagePrice·bwPagePrice 와
// 같은 값으로 내보내는 삭제 예정 필드라 받지 않는다.
export const PrintDocumentProductSchema = _ProductCommonSchema.extend({
  type: z.literal('PRINT_DOCUMENT'),
  colorPagePrice: z.number(),
  bwPagePrice: z.number(),
  coverPrice: z.number(),
  colorPageSupplyPrice: z.number(),
  bwPageSupplyPrice: z.number(),
  coverSupplyPrice: z.number(),
  minPageNumber: z.number(),
  maxPageNumber: z.number(),
})
export type PrintDocumentProduct = z.infer<typeof PrintDocumentProductSchema>

export const KmmProductSchema = _ProductCommonSchema.extend({
  type: z.literal('KMM_AWARDS'),
  imageUrl: z.string(),
  round: z.number(),
  awardProductType: AwardProductTypeSchema,
  awardProductPrice: z.number(),
  deliveryPrice: z.number(),
  orderingNumber: z.number(),
})
export type KmmProduct = z.infer<typeof KmmProductSchema>

/***************** 시그니처 교재 상품 상세 *****************/
export const SignatureWorkbookProductDetailSchema = SignatureWorkbookProductSchema.extend({
  thumbnailImageUrl: z.string(),
  index: z.string(),
  problemPageCount: z.number(),
  problemAnswerPageCount: z.number(),
  answerPageCount: z.number(),
  problemColorPrice: z.number(),
  problemBwPrice: z.number(),
  problemColorDiscountedPrice: z.number(),
  problemBwDiscountedPrice: z.number(),
  problemAnswerColorPrice: z.number(),
  problemAnswerBwPrice: z.number(),
  problemAnswerColorDiscountedPrice: z.number(),
  problemAnswerBwDiscountedPrice: z.number(),
  // N제처럼 여러 권으로 나뉘어 인쇄되는 교재
  isPartedWorkbook: z.boolean(),
  // 미리보기 페이지. 서버가 최대 30쪽까지 보낸다
  pages: z.array(z.object({ page: z.number(), imageUrl: z.string() })),
})
export type SignatureWorkbookProductDetail = z.infer<typeof SignatureWorkbookProductDetailSchema>

/***************** 맞춤 인쇄물 예상 금액 *****************/
// expectedProductAmount 는 서버가 expectedProductSupplyAmount 와 같은 값으로 덧붙이는 필드라 받지 않는다.
export const PrintDocumentExpectedAmountSchema = z.object({
  expectedProductSupplyAmount: z.number(),
  expectedDeliveryAmount: z.number(),
})
export type PrintDocumentExpectedAmount = z.infer<typeof PrintDocumentExpectedAmountSchema>
